import { useState } from 'react'
import { AlertCircle, KeyRound, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  useApiClients,
  useCreateApiClient,
  useIssueApiClientToken,
  useRevokeApiClientToken,
  useUpdateApiClient,
} from '../hooks/use-api-clients'
import { CreateApiClientDialog } from '../components/create-api-client-dialog'
import { OneTimeSecretDialog } from '../components/one-time-secret-dialog'
import { activeTokenCount, scopeLabelKey } from '../lib/scopes'
import type { OneTimeSecret } from '../components/one-time-secret-dialog'
import type { ApiClient, ApiClientToken } from '@/api/endpoints/api-clients'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { ROLES } from '@/shared/constants/roles'
import { formatDate, formatRelativeTime } from '@/shared/utils/format-date'

type PendingConfirm =
  | { kind: 'revoke'; client: ApiClient; token: ApiClientToken }
  | { kind: 'deactivate'; client: ApiClient }

/**
 * Settings → Integrationen (CHG-2026-09-webbill-delivery-import): API clients for machine callers
 * such as the Bots EDI import. Tenant admins only — the route is gated by the capability, and this
 * page additionally by role, mirroring the backend.
 */
export function IntegrationsPage() {
  const { hasRole } = useAuth()
  const { t } = useTranslation('integrations')
  const { t: tCommon } = useTranslation('common')
  const isTenantAdmin =
    hasRole(ROLES.TENANT_ADMIN) || hasRole(ROLES.SUPER_ADMIN)

  const { data, isLoading, error } = useApiClients(isTenantAdmin)
  const [createOpen, setCreateOpen] = useState(false)
  const [secret, setSecret] = useState<OneTimeSecret | null>(null)
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null)

  const create = useCreateApiClient((issued) => {
    setCreateOpen(false)
    setSecret({ clientName: issued.client.name, token: issued.token })
  })
  const issue = useIssueApiClientToken((issued) =>
    setSecret({ clientName: issued.client.name, token: issued.token }),
  )
  const update = useUpdateApiClient()
  const revoke = useRevokeApiClientToken()

  if (!isTenantAdmin) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tCommon('accessDenied')}</AlertTitle>
        <AlertDescription>{t('tenantAdminOnly')}</AlertDescription>
      </Alert>
    )
  }

  const clients = data?.items ?? []
  const maxActive = data?.maxActiveTokens ?? 2

  function runConfirm() {
    if (!confirm) return
    if (confirm.kind === 'revoke') {
      revoke.mutate({ clientId: confirm.client.id, tokenId: confirm.token.id })
    } else {
      update.mutate({ id: confirm.client.id, body: { active: false } })
    }
    setConfirm(null)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">{t('title')}</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t('description')}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('newClient')}
        </Button>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('loadFailed')}</AlertTitle>
        </Alert>
      )}

      {!isLoading && !error && clients.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <KeyRound className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">{t('empty.title')}</p>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('empty.description')}
            </p>
          </CardContent>
        </Card>
      )}

      {clients.map((client) => {
        const active = activeTokenCount(client.tokens)
        const atLimit = active >= maxActive
        return (
          <Card key={client.id}>
            <CardContent className="flex flex-col gap-4 pt-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-base font-semibold">
                      {client.name}
                    </h2>
                    <Badge variant={client.active ? 'default' : 'secondary'}>
                      {client.active
                        ? t('status.active')
                        : t('status.inactive')}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {client.scopes.map((scope) => (
                      <Badge key={scope} variant="outline" title={scope}>
                        {t(scopeLabelKey(scope), { defaultValue: scope })}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {client.lastUsedAt
                      ? t('lastUsed', {
                          time: formatRelativeTime(client.lastUsedAt),
                        })
                      : t('neverUsed')}
                    {' · '}
                    {t('createdBy', {
                      actor: client.createdBy ?? '–',
                      date: formatDate(client.createdAt),
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={atLimit || issue.isPending}
                    title={
                      atLimit ? t('tokenLimit', { max: maxActive }) : undefined
                    }
                    onClick={() => issue.mutate(client.id)}
                  >
                    {t('issueToken')}
                  </Button>
                  {client.active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={update.isPending}
                      onClick={() => setConfirm({ kind: 'deactivate', client })}
                    >
                      {t('deactivate')}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({ id: client.id, body: { active: true } })
                      }
                    >
                      {t('activate')}
                    </Button>
                  )}
                </div>
              </div>

              {!client.active && (
                <p className="text-sm text-muted-foreground">
                  {t('inactiveHint')}
                </p>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('tokens.token')}</TableHead>
                    <TableHead>{t('tokens.created')}</TableHead>
                    <TableHead>{t('tokens.lastUsed')}</TableHead>
                    <TableHead>{t('tokens.status')}</TableHead>
                    <TableHead className="text-right">
                      <span className="sr-only">{t('tokens.actions')}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {client.tokens.map((token) => (
                    <TableRow key={token.id}>
                      <TableCell className="font-mono text-sm">
                        {token.prefix}…
                      </TableCell>
                      <TableCell className="text-sm">
                        {t('tokens.createdBy', {
                          date: formatDate(token.createdAt),
                          actor: token.createdBy ?? '–',
                        })}
                      </TableCell>
                      <TableCell className="text-sm">
                        {token.lastUsedAt
                          ? formatRelativeTime(token.lastUsedAt)
                          : t('neverUsedShort')}
                      </TableCell>
                      <TableCell>
                        {token.active ? (
                          <Badge variant="outline">{t('tokens.active')}</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {t('tokens.revokedOn', {
                              date: token.revokedAt
                                ? formatDate(token.revokedAt)
                                : '–',
                            })}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {token.active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            disabled={revoke.isPending}
                            onClick={() =>
                              setConfirm({ kind: 'revoke', client, token })
                            }
                          >
                            {t('tokens.revoke')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {atLimit && (
                <p className="text-xs text-muted-foreground">
                  {t('tokenLimit', { max: maxActive })}
                </p>
              )}
            </CardContent>
          </Card>
        )
      })}

      <CreateApiClientDialog
        open={createOpen}
        availableScopes={data?.availableScopes ?? []}
        pending={create.isPending}
        onOpenChange={setCreateOpen}
        onSubmit={(body) => create.mutate(body)}
      />

      <OneTimeSecretDialog secret={secret} onClose={() => setSecret(null)} />

      <AlertDialog
        open={confirm != null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === 'revoke'
                ? t('confirmRevoke.title', { prefix: confirm.token.prefix })
                : t('confirmDeactivate.title', {
                    name: confirm?.client.name ?? '',
                  })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === 'revoke'
                ? t('confirmRevoke.description', { name: confirm.client.name })
                : t('confirmDeactivate.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm}>
              {confirm?.kind === 'revoke'
                ? t('confirmRevoke.action')
                : t('confirmDeactivate.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
